import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { fetchBuylistItems, searchBuylistItems, getBuylistAutocomplete, fetchPriceRange, fetchExpansionNames } from '../services/buylistService';
import { addToBuylist } from '../redux/buylistSlice';
import { BuylistItem, BuylistFilters, PriceFilter, PriceRangeResponse } from '../types';
import BuylistLayout from '../components/buylist/BuylistLayout';
import BuylistFilterSidebar from '../components/buylist/BuylistFilterSidebar';
import BuylistCartSidebar from '../components/buylist/BuylistCartSidebar';
import { theme, media } from '../styles/theme';
import {
  PageContainer as BasePageContainer,
  Card,
  Grid as BaseGrid,
  Input as BaseInput,
  Button as BaseButton,
  Select,
} from '../styles/components';
import BuylistItemCard from '../components/buylist/BuylistItemCard';
import { trackAddToCart, trackSearch } from '../utils/metaPixel';

const BuylistPageContainer = styled(BasePageContainer)`
  max-width: 1400px;
`;

const PageLayout = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: flex-start;

  ${media.tablet} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const SearchContainer = styled.div`
  margin-bottom: ${theme.spacing.lg};
  position: relative;
`;

const SearchInput = styled(BaseInput)`
  padding: ${theme.spacing.sm};
  position: relative;
  z-index: 1;
`;

const AutocompleteDropdown = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-top: none;
  border-radius: 0 0 ${theme.borderRadius.md} ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.medium};
  list-style-type: none;
  padding: 0;
  margin: 0;
  max-height: 250px;
  overflow-y: auto;
  z-index: 10;
`;

const AutocompleteItem = styled.li`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  cursor: pointer;
  border-bottom: 1px solid ${theme.colors.gray.lightest};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${theme.colors.gray.lightest};
  }

  &.selected {
    background-color: ${theme.colors.gray.lightest};
  }
`;

const FilterBar = styled(Card)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: ${theme.spacing.md} 0;
  padding: ${theme.spacing.md};

  ${media.mobile} {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing.sm};
  }
`;

const SortControl = styled.div`
  display: flex;
  align-items: center;
`;

const SortLabel = styled.label`
  margin-right: ${theme.spacing.xs};
  font-weight: ${theme.fontWeights.semibold};
`;

const SortSelect = styled(Select)`
  padding: ${theme.spacing.xs};
`;

const PageSizeControl = styled.div`
  display: flex;
  align-items: center;
`;

const PageSizeLabel = styled.label`
  margin-right: ${theme.spacing.xs};
  font-weight: ${theme.fontWeights.semibold};
`;

const PageSizeSelect = styled(Select)`
  padding: ${theme.spacing.xs};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  max-width: 100%;
`;

const PageButton = styled(BaseButton)<{ active: boolean }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  margin: 0 ${theme.spacing.xs};
  background-color: ${props => props.active ? theme.colors.primary : theme.colors.white};
  color: ${props => props.active ? theme.colors.white : theme.colors.text};
  border: 1px solid ${theme.colors.border};

  &:hover {
    background-color: ${props => props.active ? theme.colors.primary : theme.colors.gray.lightest};
    filter: ${props => props.active ? 'brightness(0.9)' : 'none'};
  }

  &:disabled {
    background-color: ${theme.colors.gray.light};
    color: ${theme.colors.gray.medium};
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  text-align: center;
  color: ${theme.colors.gray.dark};
  margin-top: ${theme.spacing.sm};
`;

const ItemsGrid = styled(BaseGrid)`
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: ${theme.spacing.md};

  ${media.mobile} {
    grid-template-columns: 1fr;
  }
`;


const NoItems = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.gray.dark};
  font-size: ${theme.fontSizes.lg};
`;

const CartSidebarDesktop = styled.div`
  position: sticky;
  top: calc(var(--tp-header-height) + ${theme.spacing.md});
  align-self: flex-start;

  @media (max-width: 1199px) {
    display: none;
  }
`;

const CartDrawerMobile = styled.div`
  @media (min-width: 1200px) {
    display: none;
  }
`;

// ======== Guide Section Styled Components ========

const GuideContainer = styled(Card)`
  margin-bottom: ${theme.spacing.lg};
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  border-color: rgba(255, 255, 255, 0.1);
`;

const GuideHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const GuideTitle = styled.span`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.white};
`;

const GuideChevron = styled.span<{ isOpen: boolean }>`
  font-size: ${theme.fontSizes.lg};
  color: rgba(255, 255, 255, 0.7);
  transition: transform ${theme.transitions.fast};
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const GuideBody = styled.div<{ $open: boolean }>`
  display: ${props => (props.$open ? 'block' : 'none')};
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid rgba(255, 255, 255, 0.15);
`;

const GuideSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const GuideStep = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: flex-start;
`;

const GuideStepNumber = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: ${theme.borderRadius.full};
  background-color: ${theme.colors.accent};
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.bold};
`;

const GuideStepContent = styled.div`
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);

  strong {
    color: ${theme.colors.white};
  }

  a {
    color: ${theme.colors.accent};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const GuideNote = styled.div`
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.sm};
  background-color: rgba(255, 255, 255, 0.08);
  border-left: 4px solid ${theme.colors.accent};
  border-radius: ${theme.borderRadius.sm};
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;

  a {
    color: ${theme.colors.accent};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const sortOptions = [
  { value: 'buyPrice', label: 'Pris (Cash)' },
  { value: 'maxQuantity', label: 'Antall' },
];

const pageSizeOptions = [10, 20, 50, 100];

const defaultFilters: BuylistFilters = {
  reservedList: false,
  gameChanger: false,
  topCommander: false,
  topEdhCard: false,
  premodernStaple: false,
  modernStaple: false,
};

const BuylistPage: React.FC = () => {
  const [guideOpen, setGuideOpen] = useState(false);
  const [items, setItems] = useState<BuylistItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const isSelectingSuggestion = useRef(false);
  const dispatch = useDispatch();

  // Pagination state
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [sortBy, setSortBy] = useState('buyMultiplier');
  const [sortDirection, setSortDirection] = useState('desc');
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isSearchResult, setIsSearchResult] = useState(false);

  // Filter state
  const [filters, setFilters] = useState<BuylistFilters>(defaultFilters);

  // Price filter state
  const [priceFilter, setPriceFilter] = useState<PriceFilter>({ minPrice: undefined, maxPrice: undefined });
  const [priceRange, setPriceRange] = useState<PriceRangeResponse | null>(null);

  // Expansion filter state
  const [allExpansionNames, setAllExpansionNames] = useState<string[]>([]);
  const [expansionNames, setExpansionNames] = useState<string[]>([]);
  const [selectedExpansion, setSelectedExpansion] = useState('');

  // Distinguishes a failed fetch from a genuinely empty result, so the
  // prerender build (where API calls are blocked) doesn't bake in a
  // misleading "no results" message.
  const [loadFailed, setLoadFailed] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchBuylistItems(page, size, sortBy, sortDirection, filters, priceFilter, selectedExpansion || undefined);
      setLoadFailed(false);
      setItems(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);

      // Initialize quantities
      const initialQuantities: Record<number, number> = {};
      data.content.forEach(item => {
        initialQuantities[item.buylistID] = 1;
      });
      setQuantities(initialQuantities);
    } catch (error) {
      console.error('Failed to load buylist items', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [page, size, sortBy, sortDirection, filters, priceFilter, selectedExpansion]);

  useEffect(() => {
    if (!isSearchResult) {
      loadItems();
    }
  }, [loadItems, isSearchResult]);

  // Fetch price range bounds and expansion names on mount
  useEffect(() => {
    const loadPriceRange = async () => {
      try {
        const range = await fetchPriceRange();
        setPriceRange(range);
      } catch (error) {
        console.error('Failed to load price range', error);
      }
    };
    const loadExpansions = async () => {
      try {
        const names = await fetchExpansionNames();
        setAllExpansionNames(names);
        setExpansionNames(names);
      } catch (error) {
        console.error('Failed to load expansion names', error);
      }
    };
    loadPriceRange();
    loadExpansions();
  }, []);

  // Re-run search when filters change while in search mode
  useEffect(() => {
    if (isSearchResult && searchQuery.trim()) {
      const rerunSearch = async () => {
        try {
          setLoading(true);
          const data = await searchBuylistItems(searchQuery, filters, priceFilter, selectedExpansion || undefined);
          setItems(data);
          setTotalElements(data.length);
          setTotalPages(1);
          if (!selectedExpansion) deriveExpansionNames(data);

          const initialQuantities: Record<number, number> = {};
          data.forEach(item => {
            initialQuantities[item.buylistID] = 1;
          });
          setQuantities(initialQuantities);
        } catch (error) {
          console.error('Search with filters failed', error);
        } finally {
          setLoading(false);
        }
      };
      rerunSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, priceFilter, selectedExpansion]);

  useEffect(() => {
    // Handle clicks outside of the search container to close suggestions
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Fetch autocomplete suggestions whenever search query changes
    const fetchSuggestions = async () => {
      if (searchQuery.trim().length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        const suggestionData = await getBuylistAutocomplete(searchQuery);
        setSuggestions(suggestionData);
        if (!isSelectingSuggestion.current) {
          setShowSuggestions(true);
        }
        isSelectingSuggestion.current = false;
        setSelectedSuggestionIndex(-1);
      } catch (error) {
        console.error('Failed to fetch suggestions', error);
        setSuggestions([]);
      }
    };

    const debounceTimeout = setTimeout(() => {
      fetchSuggestions();
    }, 150);

    return () => clearTimeout(debounceTimeout);
  }, [searchQuery]);

  const deriveExpansionNames = (items: BuylistItem[]) => {
    const names = Array.from(new Set(items.map(item => item.expansionName).filter(Boolean))).sort();
    setExpansionNames(names);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      // If search is cleared, go back to paginated mode
      setIsSearchResult(false);
      setExpansionNames(allExpansionNames);
      setSelectedExpansion('');
      setPage(0);
      return;
    }

    try {
      setLoading(true);
      setShowSuggestions(false);

      const data = await searchBuylistItems(searchQuery, filters, priceFilter, selectedExpansion || undefined);
      setItems(data);
      setIsSearchResult(true);
      setTotalElements(data.length);
      setTotalPages(1);
      trackSearch(searchQuery);
      if (!selectedExpansion) deriveExpansionNames(data);

      const initialQuantities: Record<number, number> = {};
      data.forEach(item => {
        initialQuantities[item.buylistID] = 1;
      });
      setQuantities(initialQuantities);
    } catch (error) {
      console.error('Search failed', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (itemId: number, value: number) => {
    const item = items.find(item => item.buylistID === itemId);
    const maxQty = item?.maxQuantity || 1;
    const boundedValue = Math.max(1, Math.min(value, maxQty));

    setQuantities(prev => ({
      ...prev,
      [itemId]: boundedValue
    }));
  };

  const handleAddToBuylist = (item: BuylistItem) => {
    const quantity = quantities[item.buylistID] || 1;
    dispatch(addToBuylist({ item, quantity }));
    trackAddToCart(item.blueprintName, quantity, (item.buyPrice || 0) * quantity);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      } else if (e.key === 'Enter') {
        if (selectedSuggestionIndex >= 0) {
          e.preventDefault();
          handleSelectSuggestion(suggestions[selectedSuggestionIndex]);
        } else {
          handleSearch();
        }
      } else if (e.key === 'Tab') {
        if (selectedSuggestionIndex >= 0) {
          e.preventDefault();
          handleSelectSuggestion(suggestions[selectedSuggestionIndex]);
        }
      }
    } else if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectSuggestion = async (suggestion: string) => {
    isSelectingSuggestion.current = true;
    setShowSuggestions(false);

    // Blur input so the onFocus handler doesn't reopen the dropdown
    if (searchContainerRef.current) {
      const input = searchContainerRef.current.querySelector('input');
      input?.blur();
    }

    try {
      setLoading(true);
      setSearchQuery(suggestion);

      const data = await searchBuylistItems(suggestion, filters, priceFilter, selectedExpansion || undefined);
      setItems(data);
      setIsSearchResult(true);
      setTotalElements(data.length);
      setTotalPages(1);
      trackSearch(suggestion);
      if (!selectedExpansion) deriveExpansionNames(data);

      const initialQuantities: Record<number, number> = {};
      data.forEach(item => {
        initialQuantities[item.buylistID] = 1;
      });
      setQuantities(initialQuantities);
    } catch (error) {
      console.error('Error searching with suggestion:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: BuylistFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handlePriceFilterChange = (newPriceFilter: PriceFilter) => {
    setPriceFilter(newPriceFilter);
    setPage(0);
  };

  const handleExpansionChange = (expansionName: string) => {
    setSelectedExpansion(expansionName);
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSize(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(e.target.value);
    setPage(0);
  };

  const getPageNumbers = () => {
    const maxPageButtons = 5;
    const pageNumbers = [];

    if (totalPages <= maxPageButtons) {
      for (let i = 0; i < totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(0, page - Math.floor(maxPageButtons / 2));
      let endPage = Math.min(totalPages - 1, startPage + maxPageButtons - 1);

      if (endPage - startPage < maxPageButtons - 1) {
        startPage = Math.max(0, endPage - maxPageButtons + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  return (
    <BuylistLayout>
      <Helmet>
        <title>MTG Buylist – Se hva vi betaler for kortene dine | Tryllepapp</title>
        <meta name="description" content="Stående MTG-buylist i Norge: søk opp kortene dine og se prisen med en gang. Utbetaling til konto innen 2 virkedager, eller 10 % ekstra i butikkreditt." />
        <link rel="canonical" href="https://selg.tryllepapp.no/buylist" />
        <meta property="og:title" content="MTG Buylist – Se hva vi betaler for kortene dine | Tryllepapp" />
        <meta property="og:description" content="Søk opp kortene dine og se prisen med en gang. Utbetaling innen 2 virkedager, eller 10 % ekstra i butikkreditt." />
        <meta property="og:url" content="https://selg.tryllepapp.no/buylist" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            // Answers must stay in sync with the visible guide text below.
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Hvilken tilstand må kortene være i?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Alle oppgitte priser gjelder kort i NM (Near Mint) tilstand. Kort i dårligere tilstand prises etter vår rabattabell, og vi graderer strengt etter Cardmarkets tilstandsguide. Vi kjøper ikke kort i tilstand Poor.',
                },
              },
              {
                '@type': 'Question',
                name: 'Hvordan får jeg betalt for kortene?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Du velger mellom bankoverføring til konto eller butikkreditt. Butikkreditt gir deg 10% ekstra verdi.',
                },
              },
              {
                '@type': 'Question',
                name: 'Hvor raskt får jeg utbetaling?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Utbetaling behandles innen 2 virkedager etter at kortene er mottatt og gradert.',
                },
              },
              {
                '@type': 'Question',
                name: 'Hvilke kort kjøper Tryllepapp via buylisten?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Buylisten dekker hovedsakelig nyere kort med en trendpris under €300. Vi kjøper alle kort, men eldre eller verdifulle kort som ikke finnes i listen kan du sende til samlinger@tryllepapp.no for et tilbud. Alle priser i buylisten er indikative og ikke bindende.',
                },
              },
              {
                '@type': 'Question',
                name: 'Kan jeg selge en hel liste med kort på én gang?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Ja, last opp en CSV-fil fra Manabox eller Deckbox, så finner vi kortene vi kjøper automatisk.',
                },
              },
            ],
          })}
        </script>
      </Helmet>
      <BuylistPageContainer>
        <h1 className="sr-only">MTG Buylist – selg enkeltkort til oss</h1>
        <GuideContainer>
          <GuideHeader onClick={() => setGuideOpen(!guideOpen)}>
            <GuideTitle>Slik selger du kort til oss</GuideTitle>
            <GuideChevron isOpen={guideOpen}>&#9660;</GuideChevron>
          </GuideHeader>
          {/* Kept in the DOM when collapsed so the guide content (and matching
              FAQPage schema) is present in the prerendered HTML. */}
          <GuideBody $open={guideOpen}>
              <GuideSteps>
                <GuideStep>
                  <GuideStepNumber>1</GuideStepNumber>
                  <GuideStepContent>
                    <strong>Finn kort</strong> — Bruk søkefeltet under for å finne kort, eller bla gjennom listen vår.
                    <strong>Alle oppgitte priser gjelder for kort i NM (Near Mint) tilstand.</strong>
                    {' '}Dersom kort er i dårligere tilstand enn NM, reduseres prisen etter vår rabattabell
                    (se <Link to="/terms#section-7">våre vilkår, punkt 7</Link>).
                  </GuideStepContent>
                </GuideStep>
                <GuideStep>
                  <GuideStepNumber>2</GuideStepNumber>
                  <GuideStepContent>
                    <strong>Legg til i salgskurv</strong> — Velg antall og legg til kortene i salgskurven din.
                    Du kan se salgskurven din når som helst ved å klikke på handlekurv-ikonet øverst til høyre.
                  </GuideStepContent>
                </GuideStep>
                <GuideStep>
                  <GuideStepNumber>3</GuideStepNumber>
                  <GuideStepContent>
                    <strong>Velg betalingsmetode</strong> — I salgskurven velger du mellom bankoverføring (konto) eller butikkreditt.
                    Butikkreditt gir deg 10% ekstra verdi.
                  </GuideStepContent>
                </GuideStep>
                <GuideStep>
                  <GuideStepNumber>4</GuideStepNumber>
                  <GuideStepContent>
                    <strong>Bekreft ordre</strong> — Gå gjennom ordren din i kassen, godta vilkårene og send inn.
                    Du mottar en bekreftelsesmail med instruksjoner for innsending.
                  </GuideStepContent>
                </GuideStep>
                <GuideStep>
                  <GuideStepNumber>5</GuideStepNumber>
                  <GuideStepContent>
                    <strong>Send inn kortene</strong> — Send kortene til oss i henhold til instruksjonene i bekreftelsesmailen.
                    Viktig: kun kort som er inkludert i forsendelsen vil bli betalt for — sørg for at forsendelsen samsvarer med ordren.
                  </GuideStepContent>
                </GuideStep>
                <GuideStep>
                  <GuideStepNumber>6</GuideStepNumber>
                  <GuideStepContent>
                    <strong>Gradering og utbetaling</strong> — Vi graderer alle kort strengt i henhold
                    til <a href="https://help.cardmarket.com/en/CardCondition" target="_blank" rel="noopener noreferrer">Cardmarkets tilstandsguide</a>.
                    Utbetaling behandles innen 2 virkedager etter gradering.
                  </GuideStepContent>
                </GuideStep>
              </GuideSteps>
              <GuideNote>
                Alle priser i vår buylist er indikative og ikke bindende. Vi kjøper ikke kort i tilstand Poor.
                Har du en stor liste? <Link to="/csv-opplasting">Last opp en CSV-fil</Link> fra Manabox eller Deckbox.
              </GuideNote>
              <GuideNote>
                Vår buylist dekker hovedsakelig nyere kort med en trendpris under €300.
                Vi kjøper alle kort, men ikke alle kan prises automatisk.
                Har du eldre eller verdifulle kort som ikke finnes i listen?
                Send oss en e-post på <a href="mailto:samlinger@tryllepapp.no">samlinger@tryllepapp.no</a> så gir vi deg et tilbud.
              </GuideNote>
            </GuideBody>
        </GuideContainer>

        <SearchContainer ref={searchContainerRef}>
          <SearchInput
            type="text"
            placeholder="Søk etter kort å selge..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => searchQuery.trim().length >= 3 && setShowSuggestions(true)}
          />
          {showSuggestions && (
            <AutocompleteDropdown>
              {suggestions.length > 0 ? (
                suggestions.map((suggestion, index) => (
                  <AutocompleteItem
                    key={index}
                    className={index === selectedSuggestionIndex ? 'selected' : ''}
                    onClick={() => handleSelectSuggestion(suggestion)}
                  >
                    {suggestion}
                  </AutocompleteItem>
                ))
              ) : (
                <AutocompleteItem>
                  Ingen treff funnet
                </AutocompleteItem>
              )}
            </AutocompleteDropdown>
          )}
        </SearchContainer>

        <PageLayout>
          <BuylistFilterSidebar
            filters={filters}
            onFilterChange={handleFilterChange}
            priceRange={priceRange}
            priceFilter={priceFilter}
            onPriceFilterChange={handlePriceFilterChange}
            expansionNames={expansionNames}
            selectedExpansion={selectedExpansion}
            onExpansionChange={handleExpansionChange}
          />

          <ContentArea>
            {!isSearchResult && (
              <FilterBar>
                <SortControl>
                  <SortLabel>Sorter etter:</SortLabel>
                  <SortSelect value={sortBy} onChange={handleSortChange}>
                    {sortOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SortSelect>
                </SortControl>

                <PageSizeControl>
                  <PageSizeLabel>Vis per side:</PageSizeLabel>
                  <PageSizeSelect value={size} onChange={handleSizeChange}>
                    {pageSizeOptions.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </PageSizeSelect>
                </PageSizeControl>
              </FilterBar>
            )}

            {loading ? (
              <div>Laster tryllepapp...</div>
            ) : items.length === 0 ? (
              <NoItems>
                {loadFailed
                  ? 'Kortlisten kunne ikke lastes akkurat nå. Last siden på nytt for å prøve igjen.'
                  : 'Fant ingen kort i vår kjøpsliste. Prøv et annet søkeord.'}
              </NoItems>
            ) : (
              <>
                <ItemsGrid>
                  {items.map(item => (
                    <BuylistItemCard
                      key={item.buylistID}
                      item={item}
                      quantity={quantities[item.buylistID] || 1}
                      onQuantityChange={handleQuantityChange}
                      onAddToBuylist={handleAddToBuylist}
                    />
                  ))}
                </ItemsGrid>

                {!isSearchResult && totalPages > 1 && (
                  <>
                    <Pagination>
                      <PageButton
                        disabled={page === 0}
                        active={false}
                        onClick={() => handlePageChange(0)}
                      >
                        «
                      </PageButton>
                      <PageButton
                        disabled={page === 0}
                        active={false}
                        onClick={() => handlePageChange(page - 1)}
                      >
                        ‹
                      </PageButton>

                      {getPageNumbers().map(pageNum => (
                        <PageButton
                          key={pageNum}
                          active={pageNum === page}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum + 1}
                        </PageButton>
                      ))}

                      <PageButton
                        disabled={page === totalPages - 1}
                        active={false}
                        onClick={() => handlePageChange(page + 1)}
                      >
                        ›
                      </PageButton>
                      <PageButton
                        disabled={page === totalPages - 1}
                        active={false}
                        onClick={() => handlePageChange(totalPages - 1)}
                      >
                        »
                      </PageButton>
                    </Pagination>

                    <PageInfo>
                      Viser {page * size + 1} - {Math.min((page + 1) * size, totalElements)} av {totalElements} kort
                    </PageInfo>
                  </>
                )}
              </>
            )}
          </ContentArea>

          <CartSidebarDesktop>
            <BuylistCartSidebar variant="sidebar" />
          </CartSidebarDesktop>
        </PageLayout>

        <CartDrawerMobile>
          <BuylistCartSidebar variant="drawer" />
        </CartDrawerMobile>
      </BuylistPageContainer>
    </BuylistLayout>
  );
};

export default BuylistPage;
